import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_URL } from "../../api-config";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

function getWebSocketURL(apiURL) {
  const isSecure = apiURL.startsWith("https://");
  const wsProtocol = isSecure ? "wss" : "ws";
  const wsURL = apiURL.replace(/^https?:\/\//, "");
  return `${wsProtocol}://${wsURL}/trading-api/historical/ws`;
}

// -------- util: cuantiles (interpolado lineal) --------
function quantile(values, q) {
  if (!values || !values.length) return NaN;
  const arr = [...values].sort((a, b) => a - b);
  const pos = (arr.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (arr[base + 1] !== undefined) {
    return arr[base] * (1 - rest) + arr[base + 1] * rest;
  }
  return arr[base];
}

const DEFAULT_PCTS = [0.9, 0.95, 0.99];

// ranking para filtro visual
const bucketRank = (b) =>
  b === "≥q99" ? 3 : b === "≥q95" ? 2 : b === "≥q90" ? 1 : 0;

const TicketsQuantiles = () => {
  const [ticks, setTicks] = useState([]);
  const [filter, setFilter] = useState("last_day"); // intradía por defecto
  // const [includeZeros, setIncludeZeros] = useState(false);
  const percentiles = DEFAULT_PCTS;
  const [minBucket, setMinBucket] = useState("all"); // all | q90 | q95 | q99

  // 1) Traer últimos N ticks
  const getLastTicks = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/trading-api/historical/ticks/2000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTicks(res.data || []);
    } catch (err) {
      console.error("Error al obtener ticks:", err);
    }
  };

  // 2) Filtrar por tiempo (intradía / ventanas recientes)
  const filtrarPorTiempo = (all) => {
    const ahora = new Date(
      new Date().toLocaleString("en-US", { timeZone: "America/Santiago" })
    );

    return all.filter((t) => {
      const dt = new Date(t.Datetime);
      switch (filter) {
        case "last_5_minutes":
          return ahora - dt <= 5 * 60 * 1000;
        case "last_15_minutes":
          return ahora - dt <= 15 * 60 * 1000;
        case "last_half_hour":
          return ahora - dt <= 30 * 60 * 1000;
        case "last_hour":
          return ahora - dt <= 60 * 60 * 1000;
        case "last_24_hours":
          return ahora - dt <= 24 * 60 * 60 * 1000;
        case "last_day":
        default: {
          const hoyLocal = ahora.toLocaleDateString("en-CA", {
            timeZone: "America/Santiago",
          }); // 'YYYY-MM-DD'
          return t.Fecha === hoyLocal;
        }
      }
    });
  };

  // 3) Cargar y WebSocket
  useEffect(() => {
    getLastTicks();

    const wsURL = getWebSocketURL(API_URL);
    const socket = new WebSocket(wsURL);

    socket.onopen = () => console.log("WS conectado (TicketsQuantiles)");

    socket.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "ping") return;

        const nuevo = {
          Fecha: msg.datetime.split(" ")[0], // YYYY-MM-DD
          Precio: msg.price,
          Volumen: msg.volume,
          Datetime: msg.datetime.replace(" ", "T"),
        };
        setTicks((prev) => [nuevo, ...prev].slice(0, 5000)); // cota
      } catch (e) {
        console.error("WS err:", e);
      }
    };

    socket.onclose = () => console.log("WS cerrado (TicketsQuantiles)");
    socket.onerror = (e) => console.error("WS error (TicketsQuantiles):", e);

    return () => socket.close();
  }, []);

  // 4) Preparación de datos, dirección T/P y cuantiles
  const { thresholds, visible, contadores } = useMemo(() => {
    const tks = filtrarPorTiempo(ticks).sort(
      (a, b) => new Date(a.Datetime) - new Date(b.Datetime)
    );

    // Dirección por ticket (vs. precio previo en el rango filtrado)
    const conDir = tks.map((t, i) => {
      const prev = i > 0 ? tks[i - 1] : null;
      let dir = ""; // "", "T", "P"
      if (prev) {
        if (t.Precio > prev.Precio) dir = "T";
        else if (t.Precio < prev.Precio) dir = "P";
        else dir = ""; // sin cambio
      }
      return { ...t, dir };
    });

    // arreglo de volúmenes para cuantiles
    let vols = conDir.map((t) => Number(t.Volumen) || 0);
    // if (!includeZeros) vols = vols.filter((v) => v > 0);

    const [p90, p95, p99] = [
      quantile(vols, percentiles[0] ?? 0.9),
      quantile(vols, percentiles[1] ?? 0.95),
      quantile(vols, percentiles[2] ?? 0.99),
    ];

    const thresholds = {
      q90: Number.isFinite(p90) ? p90 : NaN,
      q95: Number.isFinite(p95) ? p95 : NaN,
      q99: Number.isFinite(p99) ? p99 : NaN,
    };

    // bucket por umbrales
    const clasific = conDir.map((t) => {
      const v = Number(t.Volumen) || 0;
      let bucket = "normal";
      if (Number.isFinite(thresholds.q99) && v >= thresholds.q99) bucket = "≥q99";
      else if (Number.isFinite(thresholds.q95) && v >= thresholds.q95) bucket = "≥q95";
      else if (Number.isFinite(thresholds.q90) && v >= thresholds.q90) bucket = "≥q90";
      return { ...t, bucket };
    });

    // filtro visual según minBucket
    const minRank =
      minBucket === "q99" ? 3 : minBucket === "q95" ? 2 : minBucket === "q90" ? 1 : 0;

    const visible = clasific.filter((t) => bucketRank(t.bucket) >= minRank);

    // contadores globales (no afectado por filtro visual)
    const contadores = clasific.reduce(
      (acc, t) => {
        acc.total += 1;
        if (t.bucket !== "normal") acc["≥q90+"] += 1;
        if (t.bucket === "≥q95" || t.bucket === "≥q99") acc["≥q95+"] += 1;
        if (t.bucket === "≥q99") acc["≥q99"] += 1;
        return acc;
      },
      { total: 0, "≥q90+": 0, "≥q95+": 0, "≥q99": 0 }
    );

    return { thresholds, visible, contadores };
  }, [ticks, filter, percentiles, minBucket]);

  // 5) Chart datasets (solo visibles)
  const labels = useMemo(
    () => visible.map((t) => new Date(t.Datetime).toLocaleTimeString("es-CL")),
    [visible]
  );
  const volumes = useMemo(() => visible.map((t) => t.Volumen), [visible]);

  // color por barra según bucket
  const barColors = useMemo(
    () =>
      visible.map((t) =>
        t.bucket === "≥q99"
          ? "#ef4444" // rojo
          : t.bucket === "≥q95"
          ? "#f59e0b" // ámbar
          : t.bucket === "≥q90"
          ? "#3b82f6" // azul
          : "#9ca3af" // gris
      ),
    [visible]
  );

  const data = {
    labels,
    datasets: [
      // Barras de volumen
      {
        type: "bar",
        label: "Volumen (ticket)",
        data: volumes,
        backgroundColor: barColors,
        borderWidth: 0,
      },
      // Líneas de cuantiles (sin puntos), se repiten para cada x visible
      {
        type: "line",
        label: `q${Math.round((percentiles[0] ?? 0.9) * 100)}`,
        data: labels.map(() => thresholds.q90),
        borderColor: "#3b82f6",
        borderWidth: 1.5,
        borderDash: [6, 4],
        pointRadius: 0,
        pointHitRadius: 0,
        pointHoverRadius: 0,
      },
      {
        type: "line",
        label: `q${Math.round((percentiles[1] ?? 0.95) * 100)}`,
        data: labels.map(() => thresholds.q95),
        borderColor: "#f59e0b",
        borderWidth: 1.5,
        borderDash: [6, 4],
        pointRadius: 0,
        pointHitRadius: 0,
        pointHoverRadius: 0,
      },
      {
        type: "line",
        label: `q${Math.round((percentiles[2] ?? 0.99) * 100)}`,
        data: labels.map(() => thresholds.q99),
        borderColor: "#ef4444",
        borderWidth: 1.5,
        borderDash: [6, 4],
        pointRadius: 0,
        pointHitRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    animation: false,
    elements: {
      point: { radius: 0, hitRadius: 0, hoverRadius: 0 },
      line: { tension: 0, borderWidth: 1.5 },
      bar: { borderWidth: 0 },
    },
    plugins: {
      datalabels: { display: false },
      legend: {
        display: false,
        position: "bottom",
      },
      title: { display: true, text: "Tickets vs. Cuantiles (intradía)" },
      tooltip: {
        enabled: true,
        displayColors: false,
        mode: "point",
        intersect: true,
        // Solo tooltips para BARRAS (no para las líneas q90/q95/q99)
        filter: (item) => item.dataset.type === "bar",
        callbacks: {
          // Título: solo HH:MM:SS CL
          title: (items) => {
            const i = items[0]?.dataIndex;
            const ts = visible[i]?.Datetime;
            if (!ts) return "";
            return new Date(ts).toLocaleTimeString("es-CL", {
              hour12: false,
              timeZone: "America/Santiago",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            });
          },
          // Label: valor + T/P (sin repetir timestamp)
          label: (ctx) => {
            const i = ctx.dataIndex;
            const v = ctx.parsed.y;
            const price = visible[i]?.Precio;
            const dir = (visible[i]?.dir || "").trim(); // "T" | "P" | ""
            const val = v?.toLocaleString?.() ?? v;
            const px  = price?.toLocaleString?.("es-CL", {
              minimumFractionDigits: 1,
              maximumFractionDigits: 4,
            }) ?? price;
            return dir ? [`${val} ${dir}`, `Px: ${px}`] : [`${val}`, `Px: ${px}`];
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Hora" },
        grid: { display: false },
        ticks: { autoSkip: true, maxTicksLimit: 12, maxRotation: 0 },
      },
      y: {
        title: { display: true, text: "Volumen" },
        grid: { color: "rgba(255,255,255,0.08)" },
      },
    },
  };

  const filtrosDisponibles = [
    ["last_5_minutes", "Últimos 5m"],
    ["last_15_minutes", "Últimos 15m"],
    ["last_half_hour", "Últimos 30m"],
    ["last_hour", "Última hora"],
    ["last_24_hours", "Últimas 24h"],
    ["last_day", "Hoy"],
  ];

  const fmt = (v) => (Number.isFinite(v) ? v.toLocaleString() : "-");

  return (
    <div className="min-h-screen bg-[#101523] text-white px-6 py-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-gray-800 rounded-xl p-4 mb-4">
          <h2 className="text-lg font-bold">Tickets & Cuantiles (Intradía)</h2>
          <p className="text-sm opacity-80">
            Umbrales — q90: {fmt(thresholds.q90)} · q95: {fmt(thresholds.q95)} · q99: {fmt(thresholds.q99)}
          </p>
          <p className="text-xs opacity-60">
            Mostrando {visible.length} de {contadores.total} ticks (según filtro de prioridad).
          </p>
        </div>

        {/* Controles */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex gap-2">
            {filtrosDisponibles.map(([val, label]) => (
              <button
                key={val}
                onClick={() => setFilter(val)}
                className={`px-3 py-1.5 rounded-lg text-sm ${
                  filter === val ? "bg-blue-700" : "bg-blue-500"
                }`}
              >
                {label}
              </button>
            ))}
          </div>



          {/* Prioridad visual por bucket */}
          <div className="flex items-center gap-2 text-sm bg-gray-800 p-2 rounded-lg">
            <span>Mostrar:</span>
            <select
              value={minBucket}
              onChange={(e) => setMinBucket(e.target.value)}
              className="bg-gray-900 rounded px-2 py-1"
            >
              <option value="all">Todos</option>
              <option value="q90">≥ q90</option>
              <option value="q95">≥ q95</option>
              <option value="q99">≥ q99</option>
            </select>
          </div>
        </div>

        {/* Chart */}
        <div className="h-[420px] w-full bg-gray-900 rounded-xl p-3 mb-6">
          <Bar data={data} options={options} />
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div className="bg-gray-800 rounded-lg p-3">
            <div className="text-xs opacity-70">Ticks totales</div>
            <div className="text-2xl font-semibold">{contadores.total}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-3">
            <div className="text-xs opacity-70">≥ q90</div>
            <div className="text-2xl font-semibold">{contadores["≥q90+"]}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-3">
            <div className="text-xs opacity-70">≥ q95</div>
            <div className="text-2xl font-semibold">{contadores["≥q95+"]}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-3">
            <div className="text-xs opacity-70">≥ q99</div>
            <div className="text-2xl font-semibold">{contadores["≥q99"]}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketsQuantiles;
